import { SidePanel } from '@carbon/ibm-products';
import {
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineNotification,
} from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import { useAtomValue, useSetAtom } from 'jotai';
import { removeFileAtom, uploadedFilesAtom } from './atoms';
import styles from './bulk-upload.module.scss';
import { ACCEPTED_EXTENSIONS, useUploadFiles } from './hooks';

interface UploadPanelProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The Upload File side panel. Files accumulate: each is listed with its own
 * remove control, which takes out only that file's rows. A file that loads
 * cleanly closes the panel; one with errors or warnings keeps it open so they
 * can be read.
 * @param {UploadPanelProps} props - open and onClose
 * @return {JSX.Element} The side panel
 */
export const UploadPanel = ({ open, onClose }: UploadPanelProps) => {
  const files = useAtomValue(uploadedFilesAtom);
  const removeFile = useSetAtom(removeFileAtom);
  const uploadFiles = useUploadFiles();

  const handleAddFiles = async (
    _event: unknown,
    { addedFiles }: { addedFiles: File[] }
  ) => {
    if (!addedFiles.length) return;
    const outcomes = await uploadFiles(addedFiles);
    const clean = outcomes.every(
      (outcome) => outcome.status === 'loaded' && !outcome.warnings.length
    );
    if (clean) onClose();
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Upload File"
      slideIn
      selectorPageContent="#main-content"
      placement="right"
      size="md"
    >
      <div className={styles.uploadPanelBody}>
        <p className={styles.uploadPanelHelper}>
          Supported formats: .csv, .xlsx. Max file size 10MB.
        </p>
        <FileUploaderDropContainer
          labelText="Drag and drop a file here or click to upload"
          accept={ACCEPTED_EXTENSIONS}
          multiple
          onAddFiles={handleAddFiles}
        />
        {files.map((file) => (
          <div key={file.id} className={styles.uploadedFile}>
            <FileUploaderItem
              uuid={file.id}
              name={file.name}
              status={file.status === 'uploading' ? 'uploading' : 'edit'}
              iconDescription="Remove file"
              invalid={file.status === 'failed'}
              errorSubject={
                file.status === 'failed' ? 'Nothing was loaded' : undefined
              }
              errorBody={file.errors.join(' ')}
              onDelete={() => removeFile(file.id)}
            />
            {file.status === 'loaded' && (
              <p className={styles.uploadedFileCount}>
                <FormattedCount value={file.rowCount} />{' '}
                {file.rowCount === 1 ? 'row' : 'rows'} loaded
              </p>
            )}
            {file.warnings.map((warning) => (
              <InlineNotification
                key={warning}
                kind="warning"
                lowContrast
                hideCloseButton
                title={warning}
              />
            ))}
          </div>
        ))}
      </div>
    </SidePanel>
  );
};
